import { useRef } from "react";
import {
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import styles from "./styles.module.css";
import { ReactComponent as DeleteIcon } from "images/svg/delete-02.svg";
import { ReactComponent as AddIcon } from "images/svg/add-circle.svg";
import { roundOff } from "helpers/NumberFormatHelper";
import { KEYBOARD_KEYS } from "const";

const DropdownPaper = (props) => {
  const { sx, ...otherProps } = props;
  return (
    <Paper
      {...otherProps}
      sx={{
        ...sx,
        "& .MuiAutocomplete-option.Mui-focused": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option[aria-selected='true']": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option[data-focus='true']": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option:hover": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
      }}
    >
      <div
        style={{
          display: "flex",
          padding: "8px 16px",
          fontWeight: "bold",
          borderBottom: "1px solid #e0e0e0",
          color: "#E50218",
          fontSize: "13px",
        }}
      >
        <div style={{ width: "60%" }}>Item</div>
        <div style={{ width: "20%", textAlign: "right" }}>Cost</div>
        <div style={{ width: "20%", textAlign: "right" }}>Price</div>
      </div>
      {props.children}
    </Paper>
  );
};

function StockDamageTable({
  saleItems,
  items,
  handleUpdateRowQuantity,
  selectedProduct,
  setSelectedProduct,
  quantity,
  setQuantity,
  handleAddProduct,
  handleDeleteRow,
  productInputRef,
}) {
  const quantityRef = useRef(null);

  const filterProductOptions = (options, { inputValue }) => {
    const search = (inputValue || "").toLowerCase().trim();
    if (!search) return options;

    const startsWith = [];
    const contains = [];

    for (const item of options) {
      const name = (item.name || "").toLowerCase();
      const plu = (item.plu || "").toString().toLowerCase();
      const ean = (item.ean || "").toString().toLowerCase();

      const nameHas = name.includes(search);
      const pluHas = plu.includes(search);
      const eanHas = ean.includes(search);

      if (!nameHas && !pluHas && !eanHas) continue;

      if (name.startsWith(search)) {
        startsWith.push({ item, rank: 0 });
      } else if (plu.startsWith(search)) {
        startsWith.push({ item, rank: 1 });
      } else if (ean.startsWith(search)) {
        startsWith.push({ item, rank: 2 });
      } else {
        contains.push(item);
      }
    }

    const sortedStarts = startsWith
      .sort((a, b) => a.rank - b.rank || (a.item.name || "").localeCompare(b.item.name || ""))
      .map((e) => e.item);

    const sortedContains = contains.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    return [...sortedStarts, ...sortedContains];
  };

  const handleProductKeyDown = (e) => {
    if (e.key === KEYBOARD_KEYS.ENTER) {
      if (selectedProduct) {
        e.preventDefault();
        setTimeout(() => quantityRef.current?.focus(), 100);
      } else {
        const inputVal = (e.target.value || "").trim();
        if (inputVal) {
          const matchedItem = (items || []).find(
            (item) =>
              (item.ean && String(item.ean).toLowerCase() === inputVal.toLowerCase()) ||
              (item.plu && String(item.plu).toLowerCase() === inputVal.toLowerCase()) ||
              (item.name && item.name.toLowerCase() === inputVal.toLowerCase()),
          );
          if (matchedItem) {
            e.preventDefault();
            setSelectedProduct(matchedItem);
            setQuantity(1);
            setTimeout(() => quantityRef.current?.focus(), 100);
          }
        }
      }
    }
  };

  const handleQuantityKeyDown = (e) => {
    if (e.key === KEYBOARD_KEYS.ENTER) {
      e.preventDefault();
      handleAddProduct();
    }
  };

  const handleRowQuantityClick = (e) => {
    e.target.select();
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  return (
    <TableContainer component={Paper} elevation={0} className={styles.innerTableContainer}>
      <Table className={styles.muiTableRoot} size="medium">
        <TableHead>
          <TableRow>
            <TableCell className={styles.columnHead} style={{ width: "60px" }}>
              Sl No
            </TableCell>
            <TableCell className={styles.columnHead}>Product</TableCell>
            <TableCell className={styles.columnHead} style={{ width: "100px" }} align="center">
              Qty
            </TableCell>
            <TableCell className={styles.columnHead} style={{ width: "120px" }} align="right">
              Cost
            </TableCell>
            <TableCell className={styles.columnHead} style={{ width: "120px" }} align="right">
              Total
            </TableCell>
            <TableCell className={styles.columnHead} style={{ width: "60px" }} align="center">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {saleItems.map((row, index) => (
            <TableRow key={row.cartId || index}>
              <TableCell className={styles.rowCell} align="center">
                {index + 1}
              </TableCell>
              <TableCell className={styles.rowCell} style={{ fontSize: "12px", fontWeight: 600 }}>
                {row.name}
              </TableCell>
              <TableCell className={styles.rowCell} style={{ padding: 0 }}>
                <TextField
                  type="number"
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  value={row.qty}
                  onChange={(e) => handleUpdateRowQuantity(row, e.target.value)}
                  onClick={handleRowQuantityClick}
                  sx={{
                    "& .MuiInputBase-input": { fontSize: "12px", padding: "6px 10px !important", textAlign: "center" },
                    width: "100%",
                  }}
                />
              </TableCell>
              <TableCell className={styles.rowCell} align="right">
                {roundOff(row.cost)}
              </TableCell>
              <TableCell className={styles.rowCell} align="right">
                {roundOff(row.payable)}
              </TableCell>
              <TableCell className={styles.rowCell} align="center">
                <IconButton size="small" onClick={() => handleDeleteRow(row)} style={{ color: "#7c7c7d" }}>
                  <DeleteIcon className={styles.deleteIcon} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}

          {/* Input Row */}
          <TableRow>
            <TableCell className={styles.rowCell} align="center">
              {saleItems.length + 1}
            </TableCell>
            <TableCell className={styles.rowCell} style={{ padding: 0 }}>
              <Autocomplete
                options={items}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                PaperComponent={DropdownPaper}
                renderOption={(props, option, state) => (
                  <li
                    {...props}
                    key={`${option.id}-${state.index}`}
                    style={{ ...props.style, fontSize: "13px", display: "flex", width: "100%" }}
                  >
                    <div style={{ width: "60%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {option.name}
                    </div>
                    <div style={{ width: "20%", textAlign: "right" }}>
                      {option.cost !== undefined && option.cost !== null ? roundOff(option.cost) : "-"}
                    </div>
                    <div style={{ width: "20%", textAlign: "right" }}>
                      {option.price !== undefined && option.price !== null ? roundOff(option.price) : "-"}
                    </div>
                  </li>
                )}
                value={selectedProduct}
                onChange={(e, val) => {
                  setSelectedProduct(val);
                  if (val) {
                    setQuantity(1);
                    setTimeout(() => quantityRef.current?.focus(), 100);
                  }
                }}
                onKeyDown={handleProductKeyDown}
                autoHighlight
                filterOptions={filterProductOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={productInputRef}
                    placeholder="(F1) Search Product"
                    variant="standard"
                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                    autoFocus
                    sx={{
                      "& .MuiInputBase-input": { fontSize: "12px" },
                      width: "100%",
                    }}
                  />
                )}
              />
            </TableCell>
            <TableCell className={styles.rowCell} style={{ padding: 0 }}>
              <TextField
                type="number"
                inputRef={quantityRef}
                onKeyDown={handleQuantityKeyDown}
                onFocus={handleFocus}
                onClick={handleFocus}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Qty"
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  "& .MuiInputBase-input": { fontSize: "12px", padding: "6px 10px !important", textAlign: "center" },
                  width: "100%",
                }}
              />
            </TableCell>
            <TableCell className={styles.rowCell} align="right">
              {selectedProduct ? roundOff(selectedProduct.cost) : ""}
            </TableCell>
            <TableCell className={styles.rowCell} align="right">
              {selectedProduct && quantity ? roundOff(selectedProduct.cost * (quantity || 1)) : ""}
            </TableCell>
            <TableCell className={styles.rowCell} align="center">
              <IconButton onClick={handleAddProduct} disabled={!selectedProduct} size="small">
                <AddIcon sx={{ color: "#4CAF50", fontSize: "18px" }} />
              </IconButton>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default StockDamageTable;
